/**
 * IndexedDB-backed store for demo video blobs.
 *
 * The demo persists most state as JSON in localStorage, but videos are too
 * large for that path (a 40s clip at our recording bitrate is ~18 MB; LS
 * tops out around 5 MB). IDB accepts Blobs natively without base64 inflation
 * and survives page reloads, matching the persistence behavior of the
 * data-URL-based photo store.
 *
 * Keys are caller-supplied uids (matching the real Cloudflare Stream uid
 * shape) so the rest of the app can treat demo videos and real videos
 * identically: the video score field is `{ videoUid }` either way.
 */

const DB_NAME = 'cheereval-demo';
const STORE_NAME = 'videos';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
  });
  return dbPromise;
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then((db) => db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
}

export async function putVideoBlob(uid: string, blob: Blob): Promise<void> {
  const store = await tx('readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.put(blob, uid);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('IndexedDB put failed'));
  });
}

export async function getVideoBlob(uid: string): Promise<Blob | null> {
  const store = await tx('readonly');
  return new Promise<Blob | null>((resolve, reject) => {
    const req = store.get(uid);
    req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB get failed'));
  });
}

export async function deleteVideoBlob(uid: string): Promise<void> {
  const store = await tx('readwrite');
  await new Promise<void>((resolve, reject) => {
    const req = store.delete(uid);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('IndexedDB delete failed'));
  });
}

/** Generate a uid with the same opaque-hex shape Cloudflare Stream returns. */
export function generateDemoVideoUid(): string {
  // crypto.randomUUID is available in all browsers we target; fall back if not.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `demo-${crypto.randomUUID().replace(/-/g, '')}`;
  }
  return `demo-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}
